import * as THREE from 'three';
import type { BallState } from './BallState';
import type { WindField } from './WindField';

export interface AerodynamicsConfig {
  readonly dragCoefficient: number;
  readonly liftCoefficient: number;
  readonly spinDecayPerSecond: number;
}

export const DEFAULT_AERODYNAMICS: AerodynamicsConfig = {
  dragCoefficient: 0.003,
  liftCoefficient: 0.00012,
  spinDecayPerSecond: 0.08,
};

/** Tunable, believable-first flight model. Constants are intentionally centralized for later launch-monitor tuning. */
export class Aerodynamics {
  constructor(private readonly config: AerodynamicsConfig = DEFAULT_AERODYNAMICS) {}

  acceleration(state: BallState, wind: WindField, elapsedSeconds: number): THREE.Vector3 {
    const windVelocity = wind.sample(state.position, elapsedSeconds);
    const relativeVelocity = state.velocity.clone().sub(windVelocity);
    const speed = relativeVelocity.length();
    if (speed < 0.001) return new THREE.Vector3();

    const drag = relativeVelocity
      .clone()
      .normalize()
      .multiplyScalar(-this.config.dragCoefficient * speed * speed);

    const spinAxis = state.spin.lengthSq() > 0.0001 ? state.spin.clone().normalize() : null;
    const lift = spinAxis
      ? new THREE.Vector3()
          .crossVectors(spinAxis, relativeVelocity)
          .normalize()
          .multiplyScalar(this.config.liftCoefficient * state.spin.length() * speed)
      : new THREE.Vector3();

    return drag.add(lift);
  }

  decaySpin(state: BallState, deltaSeconds: number): void {
    const multiplier = Math.exp(-this.config.spinDecayPerSecond * deltaSeconds);
    state.spin.multiplyScalar(multiplier);
  }
}
